// client/src/components/campaign/GeneratePlanModal.jsx
//
// "Generate campaign plan" — backward-plans the promotion campaign off the
// event date (On-Screen 2.0). Creates the marketing campaign if missing,
// applies the promotion task checklist, and scaffolds draft social posts.

import React, { useState } from 'react';
import { generateEventPlan } from '../../services/campaignsApi';

export default function GeneratePlanModal({ tenantId, eventId, eventName, onClose, onDone }) {
  const [createTasks, setCreateTasks] = useState(true);
  const [createDraftPosts, setCreateDraftPosts] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await generateEventPlan(tenantId, eventId, { createTasks, createDraftPosts });
      setResult(res.data);
      onDone?.(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Could not generate the plan');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md space-y-3 rounded-xl bg-white p-5 text-black dark:bg-neutral-900 dark:text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold">Generate campaign plan</h3>
        <p className="text-sm opacity-70">
          On-Screen will build a promotion timeline for{' '}
          <span className="font-medium">{eventName || 'this event'}</span>, working backward
          from the event date.
        </p>

        {!result && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={createTasks} onChange={(e) => setCreateTasks(e.target.checked)} />
              Add the promotion task checklist
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={createDraftPosts} onChange={(e) => setCreateDraftPosts(e.target.checked)} />
              Scaffold draft social posts (as ideas)
            </label>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15">Cancel</button>
              <button onClick={run} disabled={busy || (!createTasks && !createDraftPosts)}
                className="rounded bg-cobalt px-3 py-1.5 text-sm text-white disabled:opacity-50">
                {busy ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </>
        )}

        {result && (
          <div className="space-y-2 text-sm">
            <div className="rounded-lg bg-emerald-100 p-3 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
              Plan generated · {result.tasksCreated} task{result.tasksCreated === 1 ? '' : 's'} ·{' '}
              {result.postsCreated} draft post{result.postsCreated === 1 ? '' : 's'}
            </div>
            {(result.draftPreview || []).length > 0 && (
              <ul className="max-h-48 space-y-1 overflow-y-auto text-xs opacity-70">
                {result.draftPreview.map((d, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="capitalize">{d.pillar}</span>
                    <span>{String(d.scheduledOn).slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end pt-1">
              <button onClick={onClose} className="rounded bg-cobalt px-3 py-1.5 text-sm text-white">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
